/** @jsx jsx */

import { useMemo } from 'react';
import { jsx } from '@balance-web/core';
import type { CSSObject } from '@balance-web/core';
import { useTheme } from '@balance-web/theme';

/**
 * NOTE: a combination of approaches to resolve layout issues:
 * 1. flex support
 * 2. grid support
 */
const directionMap = {
  row: ['minHeight', 'width'],
  column: ['minWidth', 'height'],
};

type DividerProps = {
  /** Does not accept children */
  children?: never;
  /** Does not accept css */
  css?: Pick<CSSObject, 'backgroundColor'>;
  /** Display the divider vertically */
  vertical?: boolean;

  /** Height or width of the divider depending on orientation */
  width?: string | number;
  height?: string | number;
};

export const Divider = (props: DividerProps) => {
  const { semantic } = useTheme();
  const orientation = props.vertical ? 'vertical' : 'horizontal';

  const styles = useMemo(() => {
    const direction = props.vertical ? 'row' : 'column';
    const dimention = (props.vertical ? props.width : props.height) || 1;
    const [mainAxis, crossAxis] = directionMap[direction];

    return {
      alignSelf: 'stretch', // 1.
      backgroundColor: semantic.colour.surface.neutral.border,
      flexShrink: 0,
      [crossAxis]: dimention,
      [mainAxis]: '100%', // 2.
    };
  }, [props.vertical, props.width, props.height, semantic.colour.surface.neutral.border]);

  return (
    <div
      aria-orientation={orientation}
      css={styles}
      role="separator"
      {...props}
    />
  );
};
